import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  activateVersion,
  createScenario,
  createVersion,
  disableVersion,
  getVersion,
  listScenarios,
  simulateVersion,
  validateVersion,
} from '../api/scenarios';

export function useScenarioList() {
  return useQuery({ queryKey: ['scenarios'], queryFn: listScenarios, refetchOnWindowFocus: false });
}

/** Loads a scenario version's definition for the editor (D22-adjacent: no dependence on client-side cache). */
export function useScenarioVersion(scenarioId: string | null, version: number | null | undefined) {
  return useQuery({
    queryKey: ['scenarios', scenarioId, 'versions', version],
    queryFn: () => getVersion(scenarioId!, version!),
    enabled: scenarioId != null && version != null,
    refetchOnWindowFocus: false,
  });
}

function useInvalidateScenarios() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: ['scenarios'] });
}

export function useCreateScenario() {
  const invalidate = useInvalidateScenarios();
  return useMutation({
    mutationFn: (name: string) => createScenario(name),
    onSuccess: invalidate,
  });
}

export function useCreateVersion() {
  const invalidate = useInvalidateScenarios();
  return useMutation({
    mutationFn: ({ scenarioId, definition }: { scenarioId: string; definition: Record<string, unknown> }) =>
      createVersion(scenarioId, definition),
    onSuccess: invalidate,
  });
}

export function useValidateVersion() {
  return useMutation({
    mutationFn: ({ scenarioId, version }: { scenarioId: string; version: number }) =>
      validateVersion(scenarioId, version),
  });
}

export function useSimulateVersion() {
  return useMutation({
    mutationFn: ({ scenarioId, version, runs }: { scenarioId: string; version: number; runs?: number }) =>
      simulateVersion(scenarioId, version, runs),
  });
}

export function useActivateVersion() {
  const invalidate = useInvalidateScenarios();
  return useMutation({
    mutationFn: ({ scenarioId, version }: { scenarioId: string; version: number }) =>
      activateVersion(scenarioId, version),
    onSuccess: invalidate,
  });
}

export function useDisableVersion() {
  const invalidate = useInvalidateScenarios();
  return useMutation({
    mutationFn: ({ scenarioId, version }: { scenarioId: string; version: number }) =>
      disableVersion(scenarioId, version),
    onSuccess: invalidate,
  });
}
